import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import DataTable from './DataTable';
import type { DataTableColumn } from './DataTable';

interface LineItemsDialogProps<T> {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId?: (row: T) => string;
}

/** Read-only item-wise breakdown for a document's lines — reused by the Purchase Request,
 * Purchase Order, Gate Entry, and GRN list screens' "View" action. Each caller supplies its own
 * columns (line shapes differ per document), this just supplies the dialog shell + table. */
export default function LineItemsDialog<T extends { id: string }>({
  open,
  onClose,
  title,
  subtitle,
  columns,
  rows,
  getRowId,
}: LineItemsDialogProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        ) : undefined}
        <DataTable columns={columns} rows={rows} getRowId={getRowId} searchable={false} exportable={false} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
